import { useCallback, useMemo } from 'react';
import { useNotification } from './NotificationContext';
import { NotificationType } from './NotificationTypes';

export * from './NotificationTypes';
export * from './NotificationContext';
export * from './NotificationItem';
export * from './NotificationContainer';
export * from './ConfirmDialog';

// Interface cho các options khi tạo notification
interface NotificationOptions {
  duration?: number;
  autoClose?: boolean;
  onClose?: () => void;
}

interface ShowNotificationParams {
  title: string;
  message: string;
  type?: NotificationType;
  duration?: number;
  autoClose?: boolean;
  onClose?: () => void;
}

/**
 * Helpers are memoized so consumers can safely list them in dependency
 * arrays. Without memoization, each render produced fresh function
 * references — any consumer using them in useCallback/useEffect deps would
 * re-run on every render (root cause of the /admin/sources continuous-fetch
 * incident in skhcn, 2026-05-12; replicated patch).
 */
export const useNotificationService = () => {
  const { addNotification, removeNotification, clearAll } = useNotification();

  const showNotification = useCallback(
    (params: ShowNotificationParams): string => {
      const { title, message, type = 'info', ...options } = params;
      return addNotification({ title, message, type, ...options });
    },
    [addNotification]
  );

  const showSuccess = useCallback(
    (title: string, message: string, options?: NotificationOptions): string => showNotification({ title, message, type: 'success', ...options }),
    [showNotification]
  );

  const showError = useCallback(
    (title: string, message: string, options?: NotificationOptions): string => showNotification({ title, message, type: 'error', ...options }),
    [showNotification]
  );

  const showWarning = useCallback(
    (title: string, message: string, options?: NotificationOptions): string => showNotification({ title, message, type: 'warning', ...options }),
    [showNotification]
  );

  const showInfo = useCallback(
    (title: string, message: string, options?: NotificationOptions): string => showNotification({ title, message, type: 'info', ...options }),
    [showNotification]
  );

  return useMemo(
    () => ({
      showNotification,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      removeNotification,
      clearAll
    }),
    [showNotification, showSuccess, showError, showWarning, showInfo, removeNotification, clearAll]
  );
};
